import styled, { css, keyframes } from 'styled-components';

export const ModalContainer = styled.div`
	align-items: center;
	display: flex;
	flex-direction: column;
	justify-content: center;
	width: 100%;
`;

export const Title = styled.p`
	color: ${({ theme }) => theme.colors.principal.orion};
	font-size: 20px;
	font-weight: 500;
	line-height: 30px;
	margin-bottom: 8px;
	text-align: center;
`;

export const ModalFooter = styled.footer`
	align-items: center;
	display: flex;
	flex-direction: row;
	gap: 8px;
	justify-content: space-between;
	margin-top: 32px;
	width: 100%;

	button {
		border-radius: 8px;
		height: 48px;
	}
`;

export const ActionsFooter = styled.div<ContainerPausedProps>`
	align-content: center;
	display: flex;
	flex-direction: row;
	gap: 10px;
	justify-content: flex-end;
	width: 70%;

	animation: ${({ isAnimating }) =>
		isAnimating &&
		css`
			${fadeInRight} 0.3s ease-in-out forwards
		`};
`;

export const PaymentLinkButton = styled.button`
	background-color: ${({ theme }) => theme.colors.extra.crab};
	border: 1px solid ${({ theme }) => theme.colors.extra.crab};
	color: #fff;
	cursor: pointer;
	padding: 8px 14px;

	&:disabled {
		background-color: ${({ theme }) => theme.colors.principal.eris};
		border: 1px solid ${({ theme }) => theme.colors.principal.eris};
		color: ${({ theme }) => theme.colors.principal.orion};
		cursor: default;
		pointer-events: none;
	}
`;

export const ConfirmedButton = styled(PaymentLinkButton)`
	padding: 16px 45px;

	transition: 0.2s ease-in-out opacity, scale;
	width: fit-content;

	&:hover {
		opacity: 0.85;
		scale: 1.05;
	}
`;

export const ShareButton = styled.button`
	align-items: center;
	background-color: #fff;
	border: 1px solid ${({ theme }) => theme.colors.principal.ceres};
	color: ${({ theme }) => theme.colors.extra.space_700};
	cursor: pointer;
	display: flex;
	flex-direction: row;
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	gap: 8px;
	justify-content: center;
	padding: 8px 14px;
`;

export const AlertButton = styled.button<ContainerPausedProps>`
	background-color: #ff433e;
	border: 1px solid #ff433e;
	border-radius: 8px;
	color: #fff;
	cursor: pointer;
	padding: 16px 45px;
	transition: 0.2s ease-in-out opacity, scale;
	width: fit-content;

	animation: ${({ isAnimating }) =>
		isAnimating &&
		css`
			${fadeIn} 0.3s ease-in-out forwards
		`};

	&:hover {
		opacity: 0.85;
		scale: 1.05;
	}
`;

const fadeOutRight = keyframes`
	from {
		opacity: 1;
		transform: translate(0);
		visibility: visible;
	}
	to {
		opacity: 0;
		transform: translate(120%);
		visibility: hidden;
	}
`;

const fadeInRight = keyframes`
	from {
		opacity: 0;
		transform: translate(-50%);
	} 
	to {
		opacity: 1;
		transform: translate(0);
	}
`;

const fadeIn = keyframes`
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
`;

interface ContainerPausedProps {
	isAnimating?: boolean;
}

export const ContainerPaused = styled.div<ContainerPausedProps>`
	align-items: center;
	display: flex;
	flex-direction: row;
	gap: 12px;
	justify-content: space-between;
	animation: ${({ isAnimating }) =>
		isAnimating &&
		css`
			${fadeOutRight} 0.3s ease-in-out forwards
		`};
`;

export const PausedText = styled.p`
	color: ${({ theme }) => theme.colors.principal.abell};
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	white-space: nowrap;
`;

export const ReactiveButton = styled.button`
	align-items: center;
	background-color: ${({ theme }) => theme.colors.extra.success_500};
	border: none;
	color: white;
	cursor: pointer;
	display: flex;
	flex-direction: row;
	gap: 8px;
	justify-content: center;
	padding: 8px 14px;
	transition: 0.2s ease-in-out scale;
	white-space: nowrap;
	width: 100%;

	&:hover {
		scale: 1.05;
	}
`;

export const ModalFooterQr = styled.div`
	align-items: center;
	display: flex;
	flex-direction: row;
	gap: 8px;
	justify-content: end;
	margin-top: 32px;
	width: 100%;

	button {
		background-color: ${({ theme }) => theme.colors.extra.crab};
		border-color: ${({ theme }) => theme.colors.extra.crab};
		height: 48px;
		width: 55%;
	}
`;

export const Button = styled.button`
	align-items: center;
	background-color: ${({ theme }) => theme.colors.extra.crab};
	border: none;
	border-radius: 8px;
	color: ${({ theme }) => theme.colors.principal.pure};
	cursor: pointer;
	display: flex;
	flex-direction: row;
	justify-content: center;
	outline: none;
	padding: 8px 14px;

	svg {
		margin-right: 12px;
	}

	&:disabled {
		cursor: default;
		opacity: 0.2;
		pointer-events: none;
	}
`;

export const CopyButton = styled.span`
	align-items: center;
	background-color: ${({ theme }) => theme.colors.extra.crab};
	border-radius: 4px;
	color: ${({ theme }) => theme.colors.principal.pure};
	display: flex;
	font-size: 12px;
	font-weight: 400;
	height: 28px;
	justify-content: center;
	line-height: 12px;
	padding: 6px;
`;

export const TabsContainer = styled.div`
	margin-top: 32px;
	width: 100%;
`;

export const TabBody = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	width: 100%;
`;

export const LinkCopyPasteContainer = styled.div`
	align-self: stretch;
	background: var(--Secondary-Wasp-100, #f6f5ff);
	border-radius: var(--Spacing-and-rounds-Rounded-Regular, 6px);
	display: flex;
	flex-direction: column;
	gap: 4px;
	height: 67px;
	margin-top: 18px;
	padding: 10px;

	small {
		color: var(--Grayscale-Space-700, #636172);
		font-feature-settings: 'liga' off, 'clig' off;
		font-size: 12px;
		font-style: normal;
		font-weight: 400;
		line-height: 13.2px;
	}
`;

export const QRContainerData = styled.div`
	align-items: center;
	display: flex;
	justify-content: space-between;

	span {
		color: var(--Grayscale-Space-800, #0f0f0f);
		font-size: 16px;
		font-style: normal;
		font-weight: 400;
		line-height: 18px;
		max-width: 85%;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	button {
		background: var(--Colour-Palette-Secondary-Wasp-500, #857bf9);
		border: none;
		border-radius: var(--Spacing-and-rounds-Rounded-Regular, 6px);
		color: white;
		cursor: pointer;
		font-size: 12px;
		justify-content: center;
		padding: 6px;
	}
`;

export const LinkPL = styled.a`
	color: var(--Grayscale-Space-800, #0f0f0f);
	font-size: 16px;
	font-style: normal;
	font-weight: 400;
	line-height: 18px;
	max-width: 85%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;

	:hover {
		color: var(--Colour-Palette-Secondary-Wasp-500, #857bf9);
		cursor: pointer;
		text-decoration: underline;
	}
`;

export const QRContainer = styled.div`
	align-items: center;
	align-self: stretch;
	background: var(--Grayscale-Space-300, #f8f8f8);
	border-radius: var(--Spacing-and-rounds-Rounded-Medium, 12px);
	display: flex;
	flex-direction: column;
	justify-content: center;
	margin-top: 18px;
	padding: 32px 16px;
`;

export const QRTitle = styled.p`
	color: ${({ theme }) => theme.colors.principal.orion};
	font-size: 18px;
	font-style: normal;
	font-weight: 500;
	line-height: normal;
`;

export const QRSubtitle = styled.p`
	color: var(--Grayscale-Space-700, #636172);
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	line-height: 15.4px;
`;

export const QRCodeContainer = styled.div`
	background-color: #f7f7ff;
	border-radius: 12px;
	margin: 16px 0;
	padding: 23px;
`;

export const QRCodeImgContainer = styled.div`
	align-items: center;
	background: var(--Secondary-Wasp-100, #f6f5ff);
	border-radius: var(--Spacing-and-rounds-Rounded-Medium, 12px);
	display: flex;
	height: 160px;
	justify-content: center;
	margin-top: 16px;
	width: 160px;

	img {
		border-radius: 8px;
		height: 114px;
		width: 114px;
	}
`;
export const QRSubtitleBottom = styled.p`
	color: var(--Grayscale-Space-700, #636172);
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	line-height: 15.4px;
	margin-top: 16px;
	text-align: center;
`;

export const ScanQRText = styled.p`
	color: ${({ theme }) => theme.colors.principal.vanth};
	font-size: 16px;
	font-weight: 400;
	line-height: 24px;
`;

export const ShareView = styled.div`
	align-items: center;
	display: flex;
	flex-direction: column;
	gap: 12px;
	justify-content: center;
	width: 100%;

	button {
		height: 48px;
		width: 100%;
	}
`;

export const BackButtonContainer = styled.div`
	align-items: center;
	align-self: flex-start;
	cursor: pointer;
	display: flex;
	margin-bottom: 24px;
	width: fit-content;

	svg {
		height: 12px;
		stroke: ${({ theme }) => theme.colors.principal.vanth};
	}
`;

export const BackText = styled.p`
	color: ${({ theme }) => theme.colors.principal.vanth};
	font-size: 14px;
	font-weight: 400;
	line-height: 21px;
	margin-left: 10px;
`;

export const SendLinkButtonContainer = styled.div`
	margin-top: 24px;
	width: 100%;

	button {
		align-items: center;
		display: flex;
		height: 48px;
		width: 100%;
	}
`;

export const ContainerForm = styled.div`
	display: grid;
	grid-template-columns: 1fr 32px 1fr;

	width: 100%;
	@media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
		display: flex;
		flex-direction: column;
	}
`;

export const DataLink = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
`;

export const HeaderStep = styled.div`
	display: flex;
`;
export const CustomDataLink = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
`;

export const TitleStep = styled.h1`
	color: var(--ETpay-Orion, var(--Colour-Palette-Primary-Beam-800, #140f33));
	font-size: 18px;
	font-style: normal;
	font-weight: 500;
	letter-spacing: -0.014px;
	line-height: 28px; /* 155.556% */
	text-align: center;
`;

export const BodyStep = styled.div`
	display: flex;
	flex-direction: row;
	gap: 10px;
	margin-top: 18px;

	@media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
		flex-wrap: wrap;
		overflow-x: auto;
	}
`;
export const ContainerAmount = styled.div`
	display: flex;
	flex-direction: row;
	gap: 16px;
`;

export const ContainerTextAndName = styled.div`
	display: flex;
	flex-direction: column;
`;
export const WarningText = styled.small`
	color: var(--Grayscale-Space-700, #636172);
	font-feature-settings: 'liga' off, 'clig' off;
	font-size: 11px;
	font-style: normal;
	font-weight: 400;
	line-height: 11.88px;
	margin-bottom: 16px;
	margin-top: 8px;
`;

export const CardForm = styled.div`
	align-items: flex-start;
	align-self: stretch;
	background-color: #f6f5ff;
	border-radius: 8px;
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 16px;
`;

export const CardField = styled.div`
	align-items: center;
	background-color: white;
	border-radius: 6px;
	display: flex;
	flex-direction: row;
	gap: 4px;
	height: 48px;
	justify-content: space-between;
	padding: 5px;
	width: 100%;

	span {
		margin-left: 8px;
	}
`;

export const ParagraphGlosa = styled.p`
	color: var(--Primary-Beam-800, #130f31);
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	line-height: 17px;
	margin-bottom: 16px;
	margin-top: 18px;
`;

export const ContainerSelect = styled.div`
	width: 140px;
`;

export const Disclaimer = styled.p`
	align-items: flex-start;
	align-self: stretch;
	color: var(--Grayscale-Space-700, #636172);
	display: flex;
	flex-direction: column;
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	gap: var(--Spacing-and-rounds-Rounded-Regular, 6px);
	line-height: 15.4px;
	margin-bottom: 16px;
`;

export const ContainerDates = styled.div`
	display: flex;
	flex-direction: row;
	gap: 16px;
	margin-bottom: 16px;
`;

export const InputContainer = styled.div`
	position: 'relative';
	width: 100%;
`;

export const DividerTabs = styled.div`
	background: #f2f2f2;
	height: 100%;
	margin: 0 auto;
	width: 1px;

	@media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
		display: none;
	}
`;

interface ContainerSelectNativeProps {
	width?: string;
}

export const ContainerSelectNative = styled.div<ContainerSelectNativeProps>`
	width: ${({ width }) => width || 'fit-content'};
`;

export const SelectNavite = styled.select<ContainerSelectNativeProps>`
	background: var(--Colour-Palette-Grayscale-Space-200, #fcfcfc);
	border: 1px solid #e6e6e6;
	border-radius: var(--Spacing-and-rounds-Rounded-Regular, 6px);
	color: var(--Primary-Beam-800, #130f31);
	font-family: ${({ theme }) => theme.fonts.stack};
	font-size: 16px;
	font-style: normal;
	font-weight: 400;
	height: 48px;
	line-height: 18px;
	padding: 8px;
	width: ${({ width }) => width || 'max-content'};

	option {
		color: #130f31 !important;
	}
`;

interface InputNativeProps extends React.InputHTMLAttributes<HTMLInputElement> {
	hasError?: boolean;
	icon?: React.ReactNode;
}

export const InputNative = styled.input<InputNativeProps>`
	background-color: ${({ theme, hasError }) =>
		hasError ? theme.colors.extra.error_100 : theme.colors.principal.pure};
	border: 1px solid
		${({ theme, hasError }) =>
			hasError ? theme.colors.extra.error_500 : theme.colors.principal.ceres};
	border-radius: var(--Spacing-and-rounds-Rounded-Regular, 6px);
	color: #646174;
	font-family: ${({ theme }) => theme.fonts.stack};
	font-size: 16px;
	font-style: normal;
	font-weight: 400;
	height: 48px;
	line-height: 18px;
	padding: 8px;
	width: 100%;

	&:disabled,
	&:read-only {
		background-color: ${({ theme }) => theme.colors.principal.eris};
		cursor: default;
		pointer-events: none;
	}
`;
